/**
 * Pure helpers for the "Anpassa text" screen (ADR-0102 phase 1):
 * the tracked IR → what each node may do, plain-text editing of inline
 * runs, op builders for the action log, and the "Ändringar" list rows.
 *
 * Every anchor comes from the review marks the replay stamped on the
 * tracked document (`edit.address` / `edit.fingerprint`), so a new entry
 * always points at the compiled base (or an earlier entry's output) and
 * carries the fingerprint of the node AS COMPILED.
 */

import {
  blankText,
  childLists,
  inlinePlainText,
  isLockedNode,
  isRemovable,
  groupRevertTargets,
  isTextTarget,
  logAnchorFromAddress,
  parseNodeAddress,
  undoRefusal,
} from '@hyranai/regta-document-ir';
import type {
  ActionLog,
  ActionLogEntry,
  ChangeGroup,
  BlockNode,
  DocumentIR,
  EditMark,
  InlineNode,
  LogAnchor,
  PatchConflictReason,
  ReplayReportEntry,
  ValueNode,
} from '@hyranai/regta-document-ir';
import type { FinetuneAction } from '../../../api.js';

/* ------------------------------------------------------------------ */
/* Node capabilities                                                   */
/* ------------------------------------------------------------------ */

export type NodeActions = {
  /** `replaceText` target: paragraph / heading text, or a section title. */
  edit: boolean;
  remove: boolean;
  /** `insertParagraph` after this node. */
  insertParagraph: boolean;
  /** `insertClause` after this node (sections only). */
  insertClause: boolean;
  /** `insertParagraph` with `append` (sections, groups, notices). */
  appendParagraph: boolean;
  /** Seq of the entry an "Ångra" on this node reverts (an edit or an insert). */
  undoSeq: number | null;
  /** Seq of the removal a "Återställ" on this ghost reverts. */
  restoreSeq: number | null;
  /**
   * Statutory text (ADR-0102 decision 7) that starts here: the node is
   * locked and its parent is not, so it carries the lock notice. Nested
   * locked nodes stay silent.
   */
  lockNotice: boolean;
};

const NO_ACTIONS: NodeActions = {
  edit: false,
  remove: false,
  insertParagraph: false,
  insertClause: false,
  appendParagraph: false,
  undoSeq: null,
  restoreSeq: null,
  lockNotice: false,
};

const seqOf = (patchId: string | undefined): number | null =>
  patchId && /^[1-9]\d*$/.test(patchId) ? Number(patchId) : null;

/** Effectively locked: the replay's mark (ancestors included), the node's own flag, or a signature. */
export const isLocked = (node: BlockNode): boolean => node.edit?.locked === true || isLockedNode(node);

/**
 * What the screen offers on a node of the tracked document. `insideGhost`
 * is true below a removed node (shown struck through, read-only);
 * `restoreHere` is false on every ghost of a removal but the first, so a
 * removed block instance offers one "Återställ", not one per node.
 * `parentLocked` is true below a locked node: nothing may be inserted
 * there, not even next to a child (ADR-0102 decision 7).
 */
export function nodeActions(
  node: BlockNode,
  ctx: { editable: boolean; insideGhost?: boolean; restoreHere?: boolean; parentLocked?: boolean },
): NodeActions {
  const edit = node.edit;
  const locked = isLocked(node);
  const lockNotice = locked && !ctx.parentLocked && !ctx.insideGhost && edit?.change !== 'removed';
  if (!ctx.editable || ctx.insideGhost) return { ...NO_ACTIONS, lockNotice };
  if (edit?.change === 'removed') {
    return { ...NO_ACTIONS, restoreSeq: ctx.restoreHere === false ? null : seqOf(edit.patchId) };
  }
  if (!edit?.address) return { ...NO_ACTIONS, lockNotice };
  const container = node.type === 'section' || node.type === 'group' || node.type === 'notice';
  const besideAllowed = !ctx.parentLocked;
  return {
    edit: !locked && isTextTarget(node),
    remove: !locked && isRemovable([node]),
    insertParagraph: besideAllowed && node.type !== 'section' && node.type !== 'signature',
    insertClause: besideAllowed && node.type === 'section',
    appendParagraph: !locked && container,
    undoSeq: edit.change === 'edited' || edit.change === 'inserted' ? seqOf(edit.patchId) : edit.movedBy ? seqOf(edit.movedBy) : null,
    restoreSeq: null,
    lockNotice,
  };
}

/** The log entry behind a node's change mark (edited, inserted, removed or moved), or null. */
export function changeSeq(edit: EditMark | undefined): number | null {
  if (!edit) return null;
  if (edit.change) return seqOf(edit.patchId);
  return seqOf(edit.movedBy);
}

const FIELD_OPS: ReadonlySet<string> = new Set(['defineField', 'updateField', 'removeField']);

/**
 * Where a comment written with a change anchors: the first appended
 * content entry (own fields defined in the same save come first), else the
 * first appended seq.
 */
export function commentAnchorSeq(log: ActionLog, appended: readonly number[]): number | null {
  const mine = new Set(appended);
  const content = log.entries.find((e) => mine.has(e.seq) && !FIELD_OPS.has(e.op));
  return content?.seq ?? appended[0] ?? null;
}

/**
 * The clean view's marker on a node (ADR-0102 decision 1): adapted text and
 * inserted passages get a subtle rule, removed nodes are not shown at all.
 */
export function cleanMark(node: BlockNode): 'adapted' | 'inserted' | 'hidden' | null {
  const change = node.edit?.change;
  if (change === 'removed') return 'hidden';
  if (change === 'inserted') return 'inserted';
  if (change === 'edited' || node.edit?.movedBy) return 'adapted';
  return null;
}

/** Number of adaptations in effect ("N ändringar"): change groups not fully reverted. */
export function adaptationCount(groups: ReadonlyArray<{ status: string }> | null | undefined): number {
  return (groups ?? []).filter((g) => g.status !== 'reverted').length;
}

/** "1 ändring" / "3 ändringar". */
export const changesLabel = (n: number): string => (n === 1 ? '1 ändring' : `${n} ändringar`);

/** Walks every block list of a document in render order. */
function eachNode(document: DocumentIR, visit: (node: BlockNode) => boolean | void): void {
  const walk = (list: readonly BlockNode[]): void => {
    for (const node of list) {
      if (visit(node) === false) continue;
      for (const { list: child } of childLists(node)) walk(child);
    }
  };
  for (const part of document.parts) {
    walk(part.letterhead ?? []);
    walk(part.body);
    walk(part.footer ?? []);
  }
}

/**
 * The ghosts that carry the "Återställ" button: the first removed node of
 * each removal entry, in document order. Ghosts inside a ghost never do.
 */
export function restoreAnchors(document: DocumentIR): Set<BlockNode> {
  const seen = new Set<string>();
  const out = new Set<BlockNode>();
  eachNode(document, (node) => {
    if (node.edit?.change !== 'removed') return true;
    const id = node.edit.patchId;
    if (id && !seen.has(id)) {
      seen.add(id);
      out.add(node);
    }
    return false;
  });
  return out;
}

/** The node of a tracked document at a review address, if any. */
export function findNodeByAddress(document: DocumentIR, address: string): BlockNode | null {
  let hit: BlockNode | null = null;
  eachNode(document, (node) => {
    if (hit) return false;
    if (node.edit?.address === address) hit = node;
    return true;
  });
  return hit;
}

/** Runs of consecutive top-level nodes from the same block instance. */
export function instanceRuns(nodes: readonly BlockNode[]): Array<{ instanceId: string | null; nodes: BlockNode[] }> {
  const runs: Array<{ instanceId: string | null; nodes: BlockNode[] }> = [];
  for (const node of nodes) {
    const id = node.instanceId ?? null;
    const last = runs[runs.length - 1];
    if (last && last.instanceId === id) last.nodes.push(node);
    else runs.push({ instanceId: id, nodes: [node] });
  }
  return runs;
}

/** Swedish noun for a node, for confirmations and menu labels. */
export function nodeNoun(node: BlockNode): string {
  switch (node.type) {
    case 'section':
      return node.number ? `avsnitt ${node.number}` : 'avsnittet';
    case 'paragraph':
      return 'stycket';
    case 'heading':
      return 'rubriken';
    case 'list':
      return 'listan';
    case 'optionList':
      return 'alternativen';
    case 'table':
      return 'tabellen';
    case 'detailList':
      return 'uppgiftslistan';
    case 'notice':
      return 'rutan';
    case 'group':
      return 'gruppen';
    default:
      return 'delen';
  }
}

/* ------------------------------------------------------------------ */
/* Plain-text editing of inline runs                                   */
/* ------------------------------------------------------------------ */

/** Inline nodes kept whole across a text edit, when their text survives verbatim. */
const isAtom = (n: InlineNode): boolean =>
  n.type === 'value' || n.type === 'sectionRef' || n.type === 'attachmentRef' || n.type === 'strong' || n.type === 'emphasis';

const atomText = (n: InlineNode): string =>
  n.type === 'value' && n.text == null ? blankText(n.blankWidth) : inlineText([n]);

/** What the editor shows for an inline run: values print their text, blanks their rule, line breaks `\n`. */
export function inlineText(content: readonly InlineNode[]): string {
  return content
    .map((n): string => {
      if (n.type === 'value') return n.text ?? blankText(n.blankWidth);
      if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label') return inlineText(n.content);
      return inlinePlainText([n]);
    })
    .join('');
}

/** Every value node in a run, nested ones included. */
export function valuesIn(content: readonly InlineNode[]): ValueNode[] {
  return content.flatMap((n): ValueNode[] =>
    n.type === 'value' ? [n] : n.type === 'strong' || n.type === 'emphasis' || n.type === 'label' ? valuesIn(n.content) : [],
  );
}

const textRuns = (text: string): InlineNode[] =>
  text.split('\n').flatMap((line, i): InlineNode[] => [
    ...(i > 0 ? [{ type: 'lineBreak' } as const] : []),
    ...(line ? [{ type: 'text', text: line } as const] : []),
  ]);

export type RebuiltInlines = {
  content: InlineNode[];
  /** Form values the edit turned into fixed text ("kopplas loss från formuläret"). */
  detached: ValueNode[];
  changed: boolean;
};

/**
 * Turns edited plain text back into inline content. Values, references and
 * bold / italic runs whose text is still present (in order) are kept as
 * nodes, so an untouched value stays bound to its form field and a "§ 5"
 * keeps renumbering; everything else becomes plain text. A value whose
 * text was edited or deleted is detached. Labels and document references
 * are not user-authorable and become text.
 */
export function rebuildInlines(original: readonly InlineNode[], text: string): RebuiltInlines {
  const normalized = text.replace(/\r\n?/g, '\n');
  if (normalized === inlineText(original)) return { content: [...original], detached: [], changed: false };

  const content: InlineNode[] = [];
  const detached: ValueNode[] = [];
  const origTotal = inlineText(original).length;
  let cursor = 0;
  let origOffset = 0;
  let prevOrigEnd = 0;
  for (const node of original) {
    const len = inlineText([node]).length;
    if (!isAtom(node)) {
      origOffset += len;
      continue;
    }
    const needle = atomText(node);
    // Where the atom would sit if only text after it (or only before it) changed.
    const fromStart = cursor + (origOffset - prevOrigEnd);
    const fromEnd = normalized.length - (origTotal - origOffset);
    const distance = (at: number): number => Math.min(Math.abs(at - fromStart), Math.abs(at - fromEnd));
    let best = -1;
    if (needle) {
      for (let at = normalized.indexOf(needle, cursor); at !== -1; at = normalized.indexOf(needle, at + 1)) {
        if (best === -1 || distance(at) < distance(best)) best = at;
      }
    }
    if (best === -1) {
      detached.push(...valuesIn([node]));
    } else {
      content.push(...textRuns(normalized.slice(cursor, best)), node);
      cursor = best + needle.length;
      prevOrigEnd = origOffset + len;
    }
    origOffset += len;
  }
  content.push(...textRuns(normalized.slice(cursor)));
  return { content, detached, changed: true };
}

/** Paragraph texts of a free-text block: separated by blank lines. */
export const splitParagraphs = (text: string): string[] =>
  text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

/* ------------------------------------------------------------------ */
/* Op builders                                                         */
/* ------------------------------------------------------------------ */

/** The log anchor + compiled fingerprint stamped on a tracked node. */
export function anchorOf(node: BlockNode): { anchor: LogAnchor; fingerprint: string } | null {
  const address = node.edit?.address;
  const fingerprint = node.edit?.fingerprint;
  if (!address || !fingerprint) return null;
  const anchor = logAnchorFromAddress(address);
  return anchor ? { anchor, fingerprint } : null;
}

/** A whole block instance: path-less anchor + the instance fingerprint off its first node. */
export function instanceAnchorOf(instanceId: string, first: BlockNode): { anchor: LogAnchor; fingerprint: string } | null {
  const fingerprint = first.edit?.instanceFingerprint;
  return fingerprint ? { anchor: { instanceId }, fingerprint } : null;
}

export function replaceTextAction(node: BlockNode, content: InlineNode[]): FinetuneAction | null {
  const at = anchorOf(node);
  if (!at || !isTextTarget(node)) return null;
  const payload =
    node.type === 'section' ? { content: [{ type: 'text', text: inlinePlainText(content).replace(/\s+/g, ' ').trim() } as InlineNode] } : { content };
  return { op: 'replaceText', anchor: at.anchor, fingerprint: at.fingerprint, payload };
}

export function removeAction(target: { anchor: LogAnchor; fingerprint: string }): FinetuneAction {
  return { op: 'removeNode', anchor: target.anchor, fingerprint: target.fingerprint };
}

export function insertParagraphAction(
  anchor: LogAnchor,
  text: string,
  position: 'before' | 'after' | 'append' = 'after',
): FinetuneAction | null {
  const content = textRuns(text.replace(/\r\n?/g, '\n').trim());
  if (content.length === 0) return null;
  return { op: 'insertParagraph', anchor, payload: { position, content } };
}

export function insertClauseAction(anchor: LogAnchor, title: string, body: string): FinetuneAction | null {
  const t = title.replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const paragraphs = splitParagraphs(body).map(textRuns);
  return { op: 'insertClause', anchor, payload: { position: 'after', title: t, paragraphs } };
}

export const undoActionInput = (targetSeq: number, reason: 'undo' | 'restore' | 'drop' = 'undo'): FinetuneAction => ({
  op: 'undo',
  payload: { targetSeq, reason },
});

/** Keep-as-text; the server fingerprints the node as it compiles now. */
export const keepAsTextInput = (targetSeq: number): FinetuneAction => ({
  op: 'rebase',
  payload: { targetSeq, resolution: 'keep_as_text' },
});

/* ------------------------------------------------------------------ */
/* "Ändringar" list                                                    */
/* ------------------------------------------------------------------ */

export const STATUS_LABEL: Record<ReplayReportEntry['status'], string> = {
  applied: 'tillämpad',
  conflict: 'konflikt',
  undone: 'ångrad',
  proposed: 'förslag',
  stale: 'inaktuellt förslag',
  rejected: 'avvisat förslag',
};

export const CONFLICT_REASON_TEXT: Record<PatchConflictReason, string> = {
  anchor_missing: 'Stället ändringen gällde finns inte längre i dokumentet.',
  fingerprint_mismatch:
    'Standardtexten under ändringen har ändrats sedan ändringen gjordes (t.ex. ett val i formuläret som byter formulering). Ifyllda värden ger inga konflikter.',
  invalid_target: 'Ändringen kan inte göras på den här delen av dokumentet.',
  invalid_patch: 'Ändringen är ogiltig.',
  locked: 'Texten är lagstadgad och kan inte ändras eller tas bort.',
};

/** Swedish one-line description of a log entry. Never includes contract text. */
export function describeEntry(entry: ActionLogEntry, log?: ActionLog): string {
  switch (entry.op) {
    case 'replaceText':
      return 'Text ändrad';
    case 'removeNode':
      return 'instanceId' in entry.anchor && entry.anchor.sectionId == null && !entry.anchor.path?.length
        ? 'Block borttaget'
        : 'Borttaget';
    case 'insertParagraph':
      return 'Stycke infogat';
    case 'insertClause':
      return `Eget avsnitt: ${entry.payload.title}`;
    case 'moveSection':
      return 'Avsnitt flyttat';
    case 'replaceRow':
      return 'Rad ändrad';
    case 'removeRow':
      return 'Rad borttagen';
    case 'insertRow':
      return 'Rad tillagd';
    case 'undo': {
      const r = entry.payload.reason;
      const verb = r === 'restore' ? 'Återställt' : r === 'drop' ? 'Konflikt: ändring borttagen' : 'Ångrat';
      return `${verb} #${entry.payload.targetSeq}`;
    }
    case 'rebase':
      return entry.payload.resolution === 'keep_as_text'
        ? `Konflikt: #${entry.payload.targetSeq} behålls som text`
        : `Konflikt: #${entry.payload.targetSeq} flyttad`;
    case 'defineField':
      return `Eget fält skapat: ${entry.payload.label}`;
    case 'updateField': {
      const label = entry.payload.label ?? fieldLabel(log, entry.payload.key, entry.seq);
      return label ? `Eget fält ändrat: ${label}` : 'Eget fält ändrat';
    }
    case 'removeField': {
      const label = fieldLabel(log, entry.payload.key, entry.seq);
      return label ? `Eget fält borttaget: ${label}` : 'Eget fält borttaget';
    }
    case 'acceptSuggestion':
      return `Förslag #${entry.payload.targetSeq} godtaget`;
    case 'rejectSuggestion':
      return `Förslag #${entry.payload.targetSeq} avvisat`;
  }
}

/** An own field's label as of just before `beforeSeq` (its definition and renames). */
function fieldLabel(log: ActionLog | undefined, key: string, beforeSeq: number): string | null {
  let label: string | null = null;
  for (const e of log?.entries ?? []) {
    if (e.seq >= beforeSeq) break;
    if ((e.op === 'defineField' || e.op === 'updateField') && e.payload.key === key && e.payload.label != null) label = e.payload.label;
  }
  return label;
}

export type LogRow = {
  seq: number;
  entry: ActionLogEntry;
  label: string;
  status: ReplayReportEntry['status'];
  statusLabel: string;
  report: ReplayReportEntry | null;
  /**
   * Offer "Ångra" (a content entry the group rules allow undoing — never a
   * superseded version) or "Gör om" (the group's latest undo).
   */
  revert: { label: 'Ångra' | 'Gör om'; targetSeq: number } | null;
  /** Shown in the address column for navigation; never text. */
  address: string | null;
};

/** The raw action log in order ("Visa historik"), joined with this replay's report. */
export function logRows(log: ActionLog, report: readonly ReplayReportEntry[], editable: boolean): LogRow[] {
  const bySeq = new Map(report.map((r) => [r.seq, r]));
  return log.entries.map((entry) => {
    const r = bySeq.get(entry.seq) ?? null;
    const status = r?.status ?? 'undone';
    let revert: LogRow['revert'] = null;
    if (editable && status !== 'undone' && entry.op !== 'rebase' && undoRefusal(log, entry.seq, report) == null) {
      revert = { label: entry.op === 'undo' ? 'Gör om' : 'Ångra', targetSeq: entry.seq };
    }
    return {
      seq: entry.seq,
      entry,
      label: describeEntry(entry, log),
      status,
      statusLabel:
        status === 'applied' && r?.supersededBy != null ? `ersatt av #${r.supersededBy}` : STATUS_LABEL[status],
      report: r,
      revert,
      address: r?.address ?? null,
    };
  });
}

export type GroupRow = {
  group: ChangeGroup;
  /** Swedish one-liner of the version in effect. Never contract text (except an inserted clause's title). */
  label: string;
  status: ChangeGroup['status'];
  statusLabel: string;
  /** "3 versioner" when more than one version is in effect. */
  versionsLabel: string | null;
  /** The entry that last touched the group, for author / time. */
  latest: ActionLogEntry;
  /** Where "Visa" scrolls to; `null` when nothing of the group is shown. */
  address: string | null;
  /** "Ångra senaste" / "Ångra" / "Återställ": undo of the group head. */
  undoHead: { label: string; targetSeq: number; reason: 'undo' | 'restore' } | null;
  /** "Återställ original": the batch that reverts every version. Only with more than one version. */
  revertAll: number[] | null;
  /** "Gör om": the undo entry to revert. */
  redoSeq: number | null;
  /** Entries in conflict, for the conflict list. */
  conflictSeqs: number[];
  /** The standard text under the change was updated by a newer block version and the change was kept (ADR-0103). */
  carriedOverNote: string | null;
};

export const CARRIED_OVER_NOTE = 'Standardtexten har uppdaterats – din ändring behölls.';

const GROUP_STATUS_LABEL: Record<ChangeGroup['status'], string> = {
  applied: 'tillämpad',
  conflict: 'konflikt',
  reverted: 'ångrad',
};

/**
 * The clean "Ändringar" list: one row per changed node with its version in
 * effect. Undo/redo pairs that cancel out and fully reverted groups are
 * hidden — except the group the latest action reverted, so "Gör om" stays
 * one click away. Conflicts first, then latest activity first.
 */
export function groupRows(
  log: ActionLog,
  groups: readonly ChangeGroup[],
  report: readonly ReplayReportEntry[],
  editable: boolean,
): GroupRow[] {
  const bySeq = new Map(log.entries.map((e) => [e.seq, e]));
  const status = new Map(report.map((r) => [r.seq, r.status]));
  const carried = new Set(report.filter((r) => r.status === 'applied' && r.carriedOver).map((r) => r.seq));
  const lastSeq = log.entries.reduce((max, e) => Math.max(max, e.seq), 0);
  const rows: GroupRow[] = [];
  for (const group of groups) {
    if (group.status === 'reverted' && !(group.lastSeq === lastSeq && group.redoSeq != null)) continue;
    const root = bySeq.get(group.rootSeq) as ActionLogEntry;
    const head = group.headSeq != null ? (bySeq.get(group.headSeq) as ActionLogEntry) : root;
    const inserted = group.kind === 'inserted';
    const label = inserted
      ? describeEntry(root) + (group.versions > 1 ? ' (redigerad)' : '')
      : describeEntry(head);
    const latest = bySeq.get(group.lastSeq) as ActionLogEntry;
    const canUndoHead =
      editable && group.headSeq != null && undoRefusal(log, group.headSeq, report) == null;
    const many = group.versions > 1;
    rows.push({
      group,
      label,
      status: group.status,
      statusLabel: GROUP_STATUS_LABEL[group.status],
      versionsLabel: many ? `${group.versions} versioner` : null,
      latest,
      address: group.status === 'reverted' ? null : group.address,
      undoHead: canUndoHead
        ? head.op === 'removeNode' || head.op === 'removeRow'
          ? { label: 'Återställ', targetSeq: group.headSeq as number, reason: 'restore' }
          : { label: many ? 'Ångra senaste' : 'Ångra', targetSeq: group.headSeq as number, reason: 'undo' }
        : null,
      revertAll: editable && many ? groupRevertTargets(group) : null,
      redoSeq: editable ? group.redoSeq : null,
      conflictSeqs: group.effectiveSeqs.filter((seq) => status.get(seq) === 'conflict'),
      carriedOverNote: group.status === 'applied' && group.effectiveSeqs.some((seq) => carried.has(seq)) ? CARRIED_OVER_NOTE : null,
    });
  }
  const rank = (r: GroupRow) => (r.status === 'conflict' ? 0 : r.status === 'applied' ? 1 : 2);
  return rows.sort((a, b) => rank(a) - rank(b) || b.group.lastSeq - a.group.lastSeq);
}

/** The undo batch for "Återställ original" (removals restored, everything else undone). */
export function revertAllInputs(log: ActionLog, targets: readonly number[]): FinetuneAction[] {
  const bySeq = new Map(log.entries.map((e) => [e.seq, e]));
  return targets.map((seq) => undoActionInput(seq, ['removeNode', 'removeRow'].includes(bySeq.get(seq)?.op ?? '') ? 'restore' : 'undo'));
}

/**
 * The entry an in-document "Ångra" should revert for a node marked with
 * `seq`: `seq` itself when the group rules allow it, else its group head.
 */
export function undoTargetFor(
  log: ActionLog,
  groups: readonly ChangeGroup[],
  report: readonly ReplayReportEntry[],
  seq: number,
): number | null {
  if (undoRefusal(log, seq, report) == null) return seq;
  const group = groups.find((g) => g.seqs.includes(seq));
  const head = group?.headSeq;
  return head != null && undoRefusal(log, head, report) == null ? head : null;
}

/** For "Visa original": what the entry wanted the text to say. Contract content — display only. */
export function entryText(entry: ActionLogEntry): string | null {
  switch (entry.op) {
    case 'replaceText':
    case 'insertParagraph':
      return inlineText(entry.payload.content);
    case 'insertClause':
      return [entry.payload.title, ...entry.payload.paragraphs.map((p) => inlineText(p))].join('\n\n');
    case 'replaceRow':
    case 'insertRow':
      return [
        ...(entry.payload.term ? [inlineText(entry.payload.term)] : []),
        ...(entry.payload.description ?? []).map((p) => inlineText(p)),
      ].join('\n\n');
    default:
      return null;
  }
}

/** Whether an address names a node in the base (vs. inserted output). */
export const isBaseAddress = (address: string): boolean => {
  const parsed = parseNodeAddress(address);
  return parsed != null && !('fromPatch' in parsed);
};

/** Short author label: "Du" for the viewer, the user's name when known. */
export function authorLabel(author: string, me: string | null, names: ReadonlyMap<string, string>): string {
  if (me && author === me) return 'Du';
  return names.get(author) ?? `Användare ${author.slice(0, 8)}`;
}

/** Swedish timestamp for the change list. */
export function formatAt(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

/* ------------------------------------------------------------------ */
/* Editing window                                                      */
/* ------------------------------------------------------------------ */

/**
 * ADR-0102 decision 6: the author adapts text in draft and pending ("klar
 * för granskning"); it is locked while in_review and once issued.
 */
export function adaptWindow(status: string): { allowEdit: boolean; readOnlyText: string | null } {
  if (status === 'draft' || status === 'pending') return { allowEdit: true, readOnlyText: null };
  if (status === 'in_review') {
    return {
      allowEdit: false,
      readOnlyText: 'Avtalet ligger ute för granskning – texten kan inte anpassas just nu. Ändringarna visas nedan.',
    };
  }
  return {
    allowEdit: false,
    readOnlyText: 'Avtalet kan inte längre ändras. Listan visar hur texten anpassades.',
  };
}
